import React, { useCallback, useMemo, useState } from 'react';
import {
  Empty,
  Alert,
  Card,
  Col,
  Descriptions,
  Drawer,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
  Button,
} from 'antd';

import { platformIamService } from '../../services/api';
import {
  groupPermissionsByDomain,
  isHighOrCriticalRisk,
  resolveRolePermissions,
  normalizeRiskLevel,
  resolveApiError,
  sortStrings,
} from './PlatformIamPage.utils';

const { Title, Text } = Typography;

const COVERAGE_CARDS = [
  { key: 'total_permissions', label: 'Permissoes cadastradas' },
  { key: 'total_roles', label: 'Papeis cadastrados' },
  { key: 'total_platform_users', label: 'Usuarios plataforma' },
  { key: 'users_with_roles', label: 'Usuarios com papeis' },
  { key: 'users_without_roles', label: 'Usuarios sem papeis' },
  { key: 'legacy_fallback_users', label: 'Usuarios em fallback legado' },
  { key: 'inactive_roles', label: 'Papeis inativos' },
  { key: 'inactive_permissions', label: 'Permissoes inativas' },
  { key: 'high_or_critical_permissions_count', label: 'Permissoes HIGH/CRITICAL' },
];

const PlatformIamPage = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const [mePermissions, setMePermissions] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [users, setUsers] = useState([]);

  const [selectedUser, setSelectedUser] = useState(null);
  const [effectiveLoading, setEffectiveLoading] = useState(false);
  const [effectiveError, setEffectiveError] = useState('');
  const [effectivePermissions, setEffectivePermissions] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);

  const loadData = useCallback(async (silent = false) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError('');

    try {
      const [meResponse, coverageResponse, rolesResponse, permissionsResponse, usersResponse] = await Promise.all([
        platformIamService.getIamMePermissions(),
        platformIamService.getIamCoverage(),
        platformIamService.getIamRoles(),
        platformIamService.getIamPermissions(),
        platformIamService.getIamUsers(),
      ]);

      setMePermissions(meResponse?.data || null);
      setCoverage(coverageResponse?.data || null);
      setRoles(Array.isArray(rolesResponse?.data) ? rolesResponse.data : []);
      setPermissions(Array.isArray(permissionsResponse?.data) ? permissionsResponse.data : []);
      setUsers(Array.isArray(usersResponse?.data) ? usersResponse.data : []);
    } catch (requestError) {
      setError(resolveApiError(requestError, 'Nao foi possivel carregar visibilidade IAM da plataforma.'));
    } finally {
      if (silent) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  }, []);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOpenEffectivePermissions = async (record) => {
    setSelectedUser(record);
    setEffectiveLoading(true);
    setEffectiveError('');
    setEffectivePermissions(null);

    try {
      const response = await platformIamService.getIamUserEffectivePermissions(record.id);
      setEffectivePermissions(response?.data || null);
    } catch (requestError) {
      setEffectiveError(resolveApiError(requestError, 'Nao foi possivel carregar permissoes efetivas do usuario.'));
    } finally {
      setEffectiveLoading(false);
    }
  };

  const roleColumns = [
    {
      title: 'Key',
      dataIndex: 'key',
      key: 'key',
      render: (value) => <Text code>{value}</Text>,
    },
    { title: 'Nome', dataIndex: 'name', key: 'name' },
    {
      title: 'Sistema',
      dataIndex: 'is_system',
      key: 'is_system',
      width: 120,
      render: (value) => <Tag color={value ? 'blue' : 'default'}>{value ? 'SIM' : 'NAO'}</Tag>,
    },
    {
      title: 'Ativo',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 110,
      render: (value) => <Tag color={value ? 'green' : 'red'}>{value ? 'SIM' : 'NAO'}</Tag>,
    },
    {
      title: 'Permissoes',
      dataIndex: 'permission_count',
      key: 'permission_count',
      width: 130,
    },
    {
      title: 'Usuarios',
      dataIndex: 'user_count',
      key: 'user_count',
      width: 120,
    },
    {
      title: 'Acoes',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Button size="small" onClick={() => setSelectedRole(record)}>
          Ver permissoes
        </Button>
      ),
    },
  ];

  const renderRiskTag = (value) => {
    const normalized = normalizeRiskLevel(value);
    if (!normalized) return '-';
    if (normalized === 'CRITICAL') {
      return <Tag color="red">CRITICAL</Tag>;
    }
    if (normalized === 'HIGH') {
      return <Tag color="volcano">HIGH</Tag>;
    }
    return <Tag>{normalized}</Tag>;
  };

  const permissionColumns = [
    {
      title: 'Key',
      dataIndex: 'key',
      key: 'key',
      render: (value) => <Text code>{value}</Text>,
    },
    {
      title: 'Dominio',
      dataIndex: 'domain',
      key: 'domain',
      width: 180,
      render: (value) => <Tag color="geekblue">{value || '-'}</Tag>,
    },
    {
      title: 'Risco',
      dataIndex: 'risk_level',
      key: 'risk_level',
      width: 160,
      render: (value) => {
        return renderRiskTag(value);
      },
    },
    {
      title: 'Ativo',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 120,
      render: (value) => <Tag color={value ? 'green' : 'red'}>{value ? 'SIM' : 'NAO'}</Tag>,
    },
    {
      title: 'Papeis vinculados',
      dataIndex: 'role_count',
      key: 'role_count',
      width: 160,
    },
  ];

  const userColumns = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (value) => <Text>{value}</Text>,
    },
    {
      title: 'Platform Admin',
      dataIndex: 'is_platform_admin',
      key: 'is_platform_admin',
      width: 140,
      render: (value) => <Tag color={value ? 'blue' : 'default'}>{value ? 'SIM' : 'NAO'}</Tag>,
    },
    {
      title: 'Forca troca senha',
      dataIndex: 'force_password_change',
      key: 'force_password_change',
      width: 150,
      render: (value) => <Tag color={value ? 'gold' : 'default'}>{value ? 'SIM' : 'NAO'}</Tag>,
    },
    {
      title: 'Quantidade de papeis',
      dataIndex: 'role_count',
      key: 'role_count',
      width: 160,
    },
    {
      title: 'Papeis',
      dataIndex: 'roles',
      key: 'roles',
      render: (value) => {
        const values = sortStrings(value);
        if (!values.length) {
          return <Text type="secondary">Sem papeis</Text>;
        }
        return (
          <Space size={[4, 4]} wrap>
            {values.map((roleKey) => (
              <Tag key={roleKey}>{roleKey}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Fallback legado',
      dataIndex: 'using_legacy_fallback',
      key: 'using_legacy_fallback',
      width: 170,
      render: (value) =>
        value ? (
          <Tag color="gold">ATIVO</Tag>
        ) : (
          <Tag color="default">NAO</Tag>
        ),
    },
    {
      title: 'Diagnostico',
      key: 'actions',
      width: 170,
      render: (_, record) => (
        <Button size="small" onClick={() => handleOpenEffectivePermissions(record)}>
          Ver efetivas
        </Button>
      ),
    },
  ];

  const highlightedPermissions = useMemo(
    () => permissions.filter((item) => isHighOrCriticalRisk(item?.risk_level)),
    [permissions]
  );

  const rolePermissions = useMemo(() => {
    if (!selectedRole) {
      return [];
    }
    return resolveRolePermissions(selectedRole, permissions);
  }, [selectedRole, permissions]);

  const rolePermissionsByDomain = useMemo(
    () => groupPermissionsByDomain(rolePermissions),
    [rolePermissions]
  );

  const roleMappingUnavailable = useMemo(() => {
    if (!selectedRole) return false;
    const hasRolePermissionCount = Number(selectedRole.permission_count || 0) > 0;
    return hasRolePermissionCount && rolePermissions.length === 0;
  }, [selectedRole, rolePermissions]);

  const rolePermissionColumns = [
    {
      title: 'Key',
      dataIndex: 'key',
      key: 'key',
      width: 340,
      render: (value) => <Text code>{value}</Text>,
    },
    {
      title: 'Nome / Descricao',
      key: 'name_description',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record?.name || '-'}</Text>
          <Text type="secondary">{record?.description || '-'}</Text>
        </Space>
      ),
    },
    {
      title: 'Risco',
      dataIndex: 'risk_level',
      key: 'risk_level',
      width: 140,
      render: (value) => renderRiskTag(value),
    },
    {
      title: 'Ativo',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 120,
      render: (value) => <Tag color={value ? 'green' : 'red'}>{value ? 'SIM' : 'NAO'}</Tag>,
    },
  ];

  if (loading) {
    return (
      <div style={{ minHeight: 360, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" tip="Carregando visao IAM da plataforma..." />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Title level={2} style={{ margin: 0 }}>
          IAM da Plataforma
        </Title>
        <Text type="secondary">
          Diagnostico read-only de papeis e permissoes. Edicao de papeis e permissoes sera habilitada em fase posterior.
        </Text>

        <Alert
          type="info"
          showIcon
          message="Somente leitura"
          description="Esta tela e exclusivamente de visibilidade e diagnostico. Nenhuma acao de edicao esta habilitada nesta fase."
        />

        {error ? <Alert type="error" showIcon message="Falha ao carregar" description={error} /> : null}

        {coverage?.legacy_fallback_users > 0 ? (
          <Alert
            type="warning"
            showIcon
            message="Usuarios dependentes de fallback legado"
            description={`Existem ${coverage.legacy_fallback_users} usuarios de plataforma sem papeis ativos usando fallback legado.`}
          />
        ) : null}

        {highlightedPermissions.length > 0 ? (
          <Alert
            type="warning"
            showIcon
            message="Permissoes de alto risco detectadas"
            description={`Foram encontradas ${highlightedPermissions.length} permissoes com risco HIGH/CRITICAL.`}
          />
        ) : null}

        <Card
          size="small"
          title="Meu contexto IAM"
          extra={
            <Button loading={refreshing} onClick={() => loadData(true)}>
              Atualizar
            </Button>
          }
        >
          <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }}>
            <Descriptions.Item label="User ID">
              <Text code>{mePermissions?.user_id || '-'}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Platform Admin">
              <Tag color={mePermissions?.is_platform_admin ? 'blue' : 'default'}>
                {mePermissions?.is_platform_admin ? 'SIM' : 'NAO'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Fallback legado">
              {mePermissions?.using_legacy_fallback ? <Tag color="gold">ATIVO</Tag> : <Tag>NAO</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label="Papeis" span={3}>
              <Space size={[4, 4]} wrap>
                {sortStrings(mePermissions?.roles).map((item) => (
                  <Tag key={item}>{item}</Tag>
                ))}
                {!sortStrings(mePermissions?.roles).length ? <Text type="secondary">Sem papeis</Text> : null}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Permissoes" span={3}>
              <Space size={[4, 4]} wrap>
                {sortStrings(mePermissions?.permissions).map((item) => (
                  <Tag key={item} color={item.includes('.iam.') ? 'geekblue' : 'default'}>
                    {item}
                  </Tag>
                ))}
                {!sortStrings(mePermissions?.permissions).length ? <Text type="secondary">Sem permissoes</Text> : null}
              </Space>
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Row gutter={[12, 12]}>
          {COVERAGE_CARDS.map((metric) => {
            const metricValue = Number(coverage?.[metric.key] || 0);
            const isWarningMetric = metric.key === 'legacy_fallback_users' && metricValue > 0;
            const isDangerMetric = metric.key === 'high_or_critical_permissions_count' && metricValue > 0;
            return (
              <Col xs={24} sm={12} md={8} lg={6} xl={6} key={metric.key}>
                <Card
                  size="small"
                  style={
                    isWarningMetric
                      ? { borderColor: '#faad14', boxShadow: '0 0 0 1px rgba(250, 173, 20, 0.22)' }
                      : isDangerMetric
                        ? { borderColor: '#ff7875', boxShadow: '0 0 0 1px rgba(255, 120, 117, 0.22)' }
                        : undefined
                  }
                >
                  <Statistic title={metric.label} value={metricValue} />
                </Card>
              </Col>
            );
          })}
        </Row>

        <Tabs
          items={[
            {
              key: 'roles',
              label: 'Roles',
              children: (
                <Card size="small" title="Papeis de plataforma">
                  <Table
                    size="small"
                    rowKey={(row) => row.id}
                    columns={roleColumns}
                    dataSource={roles}
                    pagination={{ pageSize: 10, showSizeChanger: false }}
                    scroll={{ x: 980 }}
                  />
                </Card>
              ),
            },
            {
              key: 'permissions',
              label: 'Permissions',
              children: (
                <Card size="small" title="Permissoes de plataforma">
                  <Table
                    size="small"
                    rowKey={(row) => row.id}
                    columns={permissionColumns}
                    dataSource={permissions}
                    pagination={{ pageSize: 12, showSizeChanger: false }}
                    scroll={{ x: 980 }}
                  />
                </Card>
              ),
            },
            {
              key: 'users',
              label: 'Users',
              children: (
                <Card size="small" title="Usuarios de plataforma">
                  <Table
                    size="small"
                    rowKey={(row) => row.id}
                    columns={userColumns}
                    dataSource={users}
                    pagination={{ pageSize: 10, showSizeChanger: false }}
                    scroll={{ x: 1280 }}
                  />
                </Card>
              ),
            },
          ]}
        />
      </Space>

      <Drawer
        title="Permissoes do papel"
        width={880}
        onClose={() => setSelectedRole(null)}
        open={Boolean(selectedRole)}
      >
        {selectedRole ? (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Alert
              type="info"
              showIcon
              message="Visualizacao somente leitura"
              description="Este painel exibe as permissoes do papel para diagnostico. Nao existem controles de edicao nesta fase."
            />

            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="Nome">{selectedRole?.name || '-'}</Descriptions.Item>
              <Descriptions.Item label="Key"><Text code>{selectedRole?.key || '-'}</Text></Descriptions.Item>
              <Descriptions.Item label="Descricao">{selectedRole?.description || '-'}</Descriptions.Item>
              <Descriptions.Item label="Ativo">
                <Tag color={selectedRole?.is_active ? 'green' : 'red'}>
                  {selectedRole?.is_active ? 'SIM' : 'NAO'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Permissoes totais">
                {Number(selectedRole?.permission_count || rolePermissions.length || 0)}
              </Descriptions.Item>
            </Descriptions>

            {roleMappingUnavailable ? (
              <Alert
                type="warning"
                showIcon
                message="Mapeamento detalhado indisponivel nos dados atuais"
                description="A API atual informa o total de permissoes por papel, mas nao expoe a lista detalhada role-permission para todos os casos."
              />
            ) : null}

            {!roleMappingUnavailable && rolePermissions.length === 0 ? (
              <Empty
                description="Este papel nao possui permissoes vinculadas."
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : null}

            {!roleMappingUnavailable && rolePermissionsByDomain.length > 0
              ? rolePermissionsByDomain.map((group) => (
                <Card
                  key={group.domain}
                  size="small"
                  title={`${group.domain} (${group.items.length})`}
                >
                  <Table
                    size="small"
                    rowKey={(row) => row.id || row.key}
                    columns={rolePermissionColumns}
                    dataSource={group.items}
                    pagination={false}
                    scroll={{ x: 920 }}
                  />
                </Card>
              ))
              : null}
          </Space>
        ) : null}
      </Drawer>

      <Drawer
        title="Permissoes efetivas do usuario"
        width={720}
        onClose={() => {
          setSelectedUser(null);
          setEffectivePermissions(null);
          setEffectiveError('');
        }}
        open={Boolean(selectedUser)}
      >
        {effectiveLoading ? <Spin tip="Carregando permissoes efetivas..." /> : null}
        {effectiveError ? <Alert type="error" showIcon message={effectiveError} /> : null}

        {!effectiveLoading && !effectiveError && effectivePermissions ? (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="Email">
                {selectedUser?.email || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="User ID">
                <Text code>{effectivePermissions.user_id}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Platform Admin">
                <Tag color={effectivePermissions.is_platform_admin ? 'blue' : 'default'}>
                  {effectivePermissions.is_platform_admin ? 'SIM' : 'NAO'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Fallback legado">
                {effectivePermissions.using_legacy_fallback ? <Tag color="gold">ATIVO</Tag> : <Tag>NAO</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="Warnings">
                {Array.isArray(effectivePermissions.warnings) && effectivePermissions.warnings.length > 0 ? (
                  <Space size={[6, 6]} wrap>
                    {effectivePermissions.warnings.map((warning) => (
                      <Tag color="gold" key={warning}>{warning}</Tag>
                    ))}
                  </Space>
                ) : (
                  <Text type="secondary">Sem warnings</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Roles">
                <Space size={[4, 4]} wrap>
                  {sortStrings(effectivePermissions.roles).map((item) => (
                    <Tag key={item}>{item}</Tag>
                  ))}
                  {!sortStrings(effectivePermissions.roles).length ? <Text type="secondary">Sem papeis</Text> : null}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Permissoes efetivas">
                <Space size={[4, 4]} wrap>
                  {sortStrings(effectivePermissions.permissions).map((item) => (
                    <Tag key={item} color={item.includes('.iam.') ? 'geekblue' : 'default'}>
                      {item}
                    </Tag>
                  ))}
                  {!sortStrings(effectivePermissions.permissions).length ? (
                    <Text type="secondary">Sem permissoes efetivas</Text>
                  ) : null}
                </Space>
              </Descriptions.Item>
            </Descriptions>
          </Space>
        ) : null}
      </Drawer>
    </div>
  );
};

export default PlatformIamPage;
